import { useMemo, useState } from "react";
import { postRestaurantTable } from "../../reservation-module/services/reservationService";
import { IDineInFloorTable } from "../models/IDineIn";

const MAX_CAPACITY = 100;
const MAX_LABEL = 100;

const ADD_ERROR: Record<string, string> = {
  DuplicateTableNumber: "Bu numarada bir masa zaten var (pasif masalar dahil). Başka bir numara seçin.",
  MinCapacityAboveCapacity: "En az kişi sayısı, kapasiteden büyük olamaz.",
  RestaurantNotFound: "Restoran bulunamadı.",
  RestourantAccessDenied: "Bu restorana masa ekleme yetkiniz yok.",
};

function errorText(err: any): string {
  const data = err?.response?.data;
  const code = Array.isArray(data) ? data[0]?.code : undefined;
  if (code && ADD_ERROR[code]) return ADD_ERROR[code];
  if (Array.isArray(data) && data[0]?.description) return data[0].description;
  const firstValidation = data?.errors && Object.values(data.errors as Record<string, string[]>)[0]?.[0];
  if (firstValidation) return firstValidation;
  if (err?.response?.status === 403) return "Bu işlem için yetkiniz yok.";
  return "Masa eklenemedi.";
}

interface Props {
  restaurantId: string;
  /** Salon görünümündeki masalar — sıradaki numarayı önermek ve çakışmayı önceden görmek için. */
  existingTables: IDineInFloorTable[];
  onClose: () => void;
  onAdded: () => void;
}

export default function AddTableModal({ restaurantId, existingTables, onClose, onAdded }: Props) {
  const nextNumber = useMemo(
    () => existingTables.reduce((max, t) => Math.max(max, t.tableNumber), 0) + 1,
    [existingTables]
  );

  const [tableNumber, setTableNumber] = useState(String(nextNumber));
  const [capacity, setCapacity] = useState(4);
  const [useMin, setUseMin] = useState(false);
  const [minCapacity, setMinCapacity] = useState(1);
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [addedCount, setAddedCount] = useState(0);

  const num = Number(tableNumber);
  const numberValid = Number.isInteger(num) && num >= 1;
  // Yalnız hizmetteki masaları biliyoruz; pasif bir masa aynı numarayı tutuyorsa sunucu reddeder.
  const duplicate = numberValid && existingTables.some((t) => t.tableNumber === num);
  const minInvalid = useMin && minCapacity > capacity;
  const canSave = numberValid && !duplicate && !minInvalid && capacity >= 1 && !saving;

  const clamp = (n: number) => Math.min(MAX_CAPACITY, Math.max(1, n));

  const save = (keepOpen: boolean) => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    postRestaurantTable({
      restourantId: restaurantId,
      tableNumber: num,
      capacity,
      // Opsiyonel alanlar: boşsa gönderilmez (backend null kabul ediyor).
      minCapacity: useMin ? minCapacity : undefined,
      label: label.trim() || undefined,
    })
      .then(() => {
        onAdded();
        if (keepOpen) {
          // Art arda masa girerken numara bir ilerler, diğer ayarlar korunur.
          setAddedCount((c) => c + 1);
          setTableNumber(String(num + 1));
          setLabel("");
        } else {
          onClose();
        }
      })
      .catch((err) => setError(errorText(err)))
      .finally(() => setSaving(false));
  };

  return (
    <div className="dinein-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="dinein-modal dinein-modal--narrow">
        <div className="dinein-modal__head">
          <div>
            <div className="dinein-modal__title"><i className="bi bi-plus-square-fill me-2"></i>Masa Ekle</div>
            <div className="dinein-modal__sub">Eklenen masa hemen hizmete girer ve salonda görünür.</div>
          </div>
          <button className="dinein-icon-btn" onClick={onClose} disabled={saving}><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="dinein-modal__body">
          {addedCount > 0 && (
            <div className="dinein-alert dinein-alert--success">
              <i className="bi bi-check-circle-fill"></i>
              <span>{addedCount} masa eklendi. Sıradaki masayı girebilirsin.</span>
            </div>
          )}

          <div className="addtbl__row">
            <div className="addtbl__field">
              <label>Masa No <span className="pc-req">*</span></label>
              <input className={`form-control addtbl__number${duplicate || (!numberValid && tableNumber !== "") ? " is-invalid" : ""}`}
                type="number" inputMode="numeric" min={1} value={tableNumber} disabled={saving}
                onChange={(e) => { setTableNumber(e.target.value); setError(""); }} />
              {duplicate && <div className="addtbl__err">Bu numara salonda zaten var.</div>}
              {!numberValid && tableNumber !== "" && <div className="addtbl__err">1 veya daha büyük bir tam sayı girin.</div>}
            </div>

            <div className="addtbl__field">
              <label>Kapasite <span className="pc-req">*</span></label>
              <div className="dinein-stepper addtbl__stepper">
                <button type="button" disabled={saving || capacity <= 1} onClick={() => setCapacity((c) => clamp(c - 1))}>
                  <i className="bi bi-dash-lg"></i>
                </button>
                <input type="number" inputMode="numeric" min={1} max={MAX_CAPACITY} value={capacity} disabled={saving}
                  onChange={(e) => setCapacity(clamp(Number(e.target.value) || 1))} />
                <button type="button" disabled={saving || capacity >= MAX_CAPACITY} onClick={() => setCapacity((c) => clamp(c + 1))}>
                  <i className="bi bi-plus-lg"></i>
                </button>
              </div>
              <div className="addtbl__hint">kişi</div>
            </div>
          </div>

          <div className="addtbl__quick">
            {[2, 4, 6, 8].map((n) => (
              <button key={n} type="button" disabled={saving}
                className={`addtbl__chip${capacity === n ? " addtbl__chip--on" : ""}`}
                onClick={() => setCapacity(n)}>
                {n} kişilik
              </button>
            ))}
          </div>

          <div className="addtbl__field">
            <label>Etiket <span className="addtbl__opt">(opsiyonel)</span></label>
            <input className="form-control" value={label} maxLength={MAX_LABEL} disabled={saving}
              placeholder="ör. Bahçe, Cam kenarı, Teras"
              onChange={(e) => setLabel(e.target.value)} />
          </div>

          <label className="po-check">
            <input type="checkbox" checked={useMin} disabled={saving} onChange={(e) => setUseMin(e.target.checked)} />
            <span>En az kişi sayısı belirle <span className="addtbl__opt">(rezervasyonda küçük grup bu masaya yerleştirilmez)</span></span>
          </label>
          {useMin && (
            <div className="addtbl__field addtbl__min">
              <div className="dinein-stepper addtbl__stepper">
                <button type="button" disabled={saving || minCapacity <= 1} onClick={() => setMinCapacity((c) => clamp(c - 1))}>
                  <i className="bi bi-dash-lg"></i>
                </button>
                <input type="number" inputMode="numeric" min={1} max={MAX_CAPACITY} value={minCapacity} disabled={saving}
                  onChange={(e) => setMinCapacity(clamp(Number(e.target.value) || 1))} />
                <button type="button" disabled={saving || minCapacity >= MAX_CAPACITY} onClick={() => setMinCapacity((c) => clamp(c + 1))}>
                  <i className="bi bi-plus-lg"></i>
                </button>
              </div>
              {minInvalid && <div className="addtbl__err">Kapasiteden ({capacity}) büyük olamaz.</div>}
            </div>
          )}

          {error && (
            <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
          )}
        </div>

        <div className="dinein-modal__foot addtbl__foot">
          <button className="dinein-btn dinein-btn--ghost" onClick={() => save(true)} disabled={!canSave}>
            <i className="bi bi-plus-lg me-1"></i>Ekle ve Devam Et
          </button>
          <button className="dinein-btn dinein-btn--primary" onClick={() => save(false)} disabled={!canSave}>
            {saving
              ? <><span className="spinner-border spinner-border-sm me-1" />Ekleniyor...</>
              : <><i className="bi bi-check-lg me-1"></i>Masayı Ekle</>}
          </button>
        </div>
      </div>
    </div>
  );
}
